import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Calculator, Zap, IndianRupee, TrendingDown, Calendar, Sun } from 'lucide-react';

export const SolarCalculator = () => {
  const [formData, setFormData] = useState({
    propertyType: '',
    monthlyBill: '',
    monthlyUnits: '',
    roofArea: '',
    location: ''
  });

  const [results, setResults] = useState(null);

  const handleInputChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  // Pricing table per kW
  const getSystemCost = (capacityKW) => {
    const kw = Math.ceil(capacityKW);
    if (kw === 1) return 70000;
    if (kw === 2) return 120000;
    if (kw === 3) return 170000;
    if (kw === 4) return 240000;
    if (kw === 5) return 275000;
    if (kw >= 6 && kw <= 10) return kw * 45000;
    if (kw >= 11 && kw <= 20) return kw * 40000;
    if (kw >= 21 && kw <= 40) return kw * 38000;
    if (kw >= 41 && kw <= 100) return kw * 36000;
    if (kw >= 101 && kw <= 1000) return kw * 33000;
    return kw * 32000; // 1001 KW and above
  };

  const calculateSavings = () => {
    if (!formData.propertyType || !formData.monthlyBill || !formData.monthlyUnits) {
      alert('Please fill in required fields: Property Type, Monthly Bill, and Monthly Units');
      return;
    }

    const monthlyBill = parseFloat(formData.monthlyBill);
    const monthlyUnits = parseFloat(formData.monthlyUnits);

    // Recommended System Size = Monthly Units / 120
    const systemSize = Math.max(1, Math.ceil(monthlyUnits / 120));

    // Annual Savings = (Monthly Bill / Monthly Units) * 120 * System Size * 12
    const tariffPerUnit = monthlyBill / monthlyUnits;
    const annualSavings = Math.round(tariffPerUnit * 120 * systemSize * 12);
    const monthlySavings = Math.round(annualSavings / 12);

    // Total system cost
    const totalCost = getSystemCost(systemSize);

    // Subsidy - only for Residential
    let subsidy = 0;
    if (formData.propertyType === 'Residential') {
      if (systemSize === 1) subsidy = 30000;
      else if (systemSize === 2) subsidy = 60000;
      else if (systemSize >= 3) subsidy = 78000;
    }

    const netCost = totalCost - subsidy;
    const paybackPeriod = (netCost / annualSavings).toFixed(1);
    const savingsIn25Years = annualSavings * 25 - netCost;

    setResults({
      systemSize,
      totalCost,
      subsidy,
      netCost: Math.round(netCost),
      tariffPerUnit: tariffPerUnit.toFixed(2),
      annualSavings,
      monthlySavings,
      paybackPeriod,
      savingsIn25Years: Math.round(savingsIn25Years),
      co2Offset: (systemSize * 1.5 * 25).toFixed(1),
      propertyType: formData.propertyType
    });
  };

  const resetCalculator = () => {
    setFormData({
      propertyType: '',
      monthlyBill: '',
      monthlyUnits: '',
      roofArea: '',
      location: ''
    });
    setResults(null);
  };

  return (
    <div className="min-h-screen pt-24 pb-16">
      {/* Hero Section */}
      <section className="py-16 px-4 bg-gradient-to-br from-orange-50 via-blue-50 to-white">
        <div className="container mx-auto text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl mb-6">
            <Calculator className="h-10 w-10 text-white" />
          </div>
          <h1 className="text-5xl lg:text-6xl font-bold mb-6">
            Solar <span className="text-orange-500">Calculator</span>
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Calculate your potential savings with solar energy. Get instant estimates for system size, 
            costs, subsidies, and return on investment.
          </p>
        </div>
      </section>

      {/* Calculator Section */}
      <section className="py-16 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="grid lg:grid-cols-2 gap-8">
            {/* Input Form */}
            <Card className="border-none shadow-2xl">
              <CardHeader className="bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-t-xl">
                <CardTitle className="text-2xl">Enter Your Details</CardTitle>
              </CardHeader>
              <CardContent className="p-8 space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="propertyType" className="text-base font-semibold">
                    Property Type <span className="text-red-500">*</span>
                  </Label>
                  <Select onValueChange={(value) => handleInputChange('propertyType', value)} value={formData.propertyType}>
                    <SelectTrigger className="w-full border-gray-300 focus:border-orange-500 focus:ring-orange-500">
                      <SelectValue placeholder="Select property type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Residential">Residential</SelectItem>
                      <SelectItem value="Commercial">Commercial</SelectItem>
                      <SelectItem value="Industrial">Industrial</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="monthlyBill" className="text-base font-semibold">
                    Average Monthly Electricity Bill (₹) <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="monthlyBill"
                    type="number"
                    placeholder="e.g., 5000"
                    value={formData.monthlyBill}
                    onChange={(e) => handleInputChange('monthlyBill', e.target.value)}
                    className="border-gray-300 focus:border-orange-500 focus:ring-orange-500" />

                </div>

                <div className="space-y-2">
                  <Label htmlFor="monthlyUnits" className="text-base font-semibold">
                    Average Monthly Units Consumed (kWh) <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="monthlyUnits"
                    type="number"
                    placeholder="e.g., 800"
                    value={formData.monthlyUnits}
                    onChange={(e) => handleInputChange('monthlyUnits', e.target.value)}
                    className="border-gray-300 focus:border-orange-500 focus:ring-orange-500" />

                  <p className="text-xs text-gray-500">Check your electricity bill for units consumed</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="roofArea" className="text-base font-semibold">
                    Available Roof Area (sq. ft.) <span className="text-gray-400">(Optional)</span>
                  </Label>
                  <Input
                    id="roofArea"
                    type="number"
                    placeholder="e.g., 500"
                    value={formData.roofArea}
                    onChange={(e) => handleInputChange('roofArea', e.target.value)}
                    className="border-gray-300 focus:border-orange-500 focus:ring-orange-500" />

                  <p className="text-xs text-gray-500">1 kW requires approximately 100 sq. ft.</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="location" className="text-base font-semibold">
                    Location <span className="text-gray-400">(Optional)</span>
                  </Label>
                  <Input
                    id="location"
                    type="text"
                    placeholder="e.g., Hyderabad"
                    value={formData.location}
                    onChange={(e) => handleInputChange('location', e.target.value)}
                    className="border-gray-300 focus:border-orange-500 focus:ring-orange-500" />

                </div>

                <div className="flex gap-4 pt-4">
                  <Button
                    onClick={calculateSavings}
                    className="flex-1 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white py-6 text-lg font-semibold rounded-xl shadow-xl">

                    <Calculator className="mr-2 h-5 w-5" />
                    Calculate Savings
                  </Button>
                  <Button
                    onClick={resetCalculator}
                    variant="outline"
                    className="px-6 border-2 border-gray-300 hover:bg-gray-50">

                    Reset
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Results */}
            <div className="space-y-6">
              {results ?
              <>
                  <Card className="border-none shadow-2xl bg-gradient-to-br from-orange-500 to-orange-600 text-white">
                    <CardContent className="p-8">
                      <h3 className="text-3xl font-bold mb-2">Your Estimated Savings</h3>
                      <div className="flex items-baseline">
                        <IndianRupee className="h-8 w-8 mr-2" />
                        <span className="text-5xl font-bold">{results.monthlySavings.toLocaleString()}</span>
                        <span className="text-2xl ml-2">/month</span>
                      </div>
                      <p className="text-orange-100 mt-2">₹{results.annualSavings.toLocaleString()}/year in electricity savings</p>
                    </CardContent>
                  </Card>

                  <Card className="border-none shadow-xl">
                    <CardHeader>
                      <CardTitle className="text-2xl flex items-center">
                        <Zap className="h-6 w-6 text-orange-600 mr-2" />
                        System Details
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="flex justify-between items-center py-3 border-b">
                        <span className="text-gray-600 font-medium">Your Current Tariff</span>
                        <span className="text-lg font-bold text-blue-600">₹{results.tariffPerUnit}/unit</span>
                      </div>
                      <div className="flex justify-between items-center py-3 border-b">
                        <span className="text-gray-600 font-medium">Recommended System Size</span>
                        <span className="text-2xl font-bold text-orange-600">{results.systemSize} kW</span>
                      </div>
                      <div className="flex justify-between items-center py-3 border-b">
                        <span className="text-gray-600 font-medium">Total System Cost</span>
                        <span className="text-xl font-bold">₹{results.totalCost.toLocaleString()}</span>
                      </div>
                      {results.subsidy > 0 &&
                    <div className="flex justify-between items-center py-3 border-b">
                          <span className="text-gray-600 font-medium">Government Subsidy</span>
                          <span className="text-xl font-bold text-green-600">- ₹{results.subsidy.toLocaleString()}</span>
                        </div>
                    }
                      <div className="flex justify-between items-center py-3 bg-orange-50 rounded-lg px-4">
                        <span className="text-gray-900 font-semibold">
                          Net Cost {results.propertyType === 'Residential' && '(After Subsidy)'}
                        </span>
                        <span className="text-2xl font-bold text-orange-600">₹{results.netCost.toLocaleString()}</span>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="border-none shadow-xl">
                    <CardHeader>
                      <CardTitle className="text-2xl flex items-center">
                        <TrendingDown className="h-6 w-6 text-orange-600 mr-2" />
                        Return on Investment
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="flex items-center justify-between py-3 border-b">
                        <div className="flex items-center">
                          <Calendar className="h-5 w-5 text-orange-500 mr-2" />
                          <span className="text-gray-600 font-medium">Payback Period</span>
                        </div>
                        <span className="text-xl font-bold text-orange-600">{results.paybackPeriod} years</span>
                      </div>
                      <div className="flex items-center justify-between py-3 border-b">
                        <div className="flex items-center">
                          <IndianRupee className="h-5 w-5 text-orange-500 mr-2" />
                          <span className="text-gray-600 font-medium">25-Year Savings</span>
                        </div>
                        <span className="text-xl font-bold text-green-600">₹{results.savingsIn25Years.toLocaleString()}</span>
                      </div>
                      <div className="flex items-center justify-between py-3">
                        <div className="flex items-center">
                          <Sun className="h-5 w-5 text-orange-500 mr-2" />
                          <span className="text-gray-600 font-medium">CO₂ Offset (25 years)</span>
                        </div>
                        <span className="text-xl font-bold text-green-600">{results.co2Offset} tons</span>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="border-none shadow-xl bg-blue-50">
                    <CardContent className="p-6">
                      <p className="!text-sm mb-4 text-gray-600">
                        <strong>Note:</strong> Pricing and Savings Note:

Project cost and final system design are subject to site conditions such as building height, roof type, and mounting structure requirements. Savings shown are indicative estimates based on average solar conditions in India and may vary depending on location, roof orientation, shading, and electricity consumption patterns.
                      
                    </p>
                      <Link to="/contact">
                        <Button className="w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white py-6 text-lg font-semibold rounded-xl">
                          Get Detailed Quote
                        </Button>
                      </Link>
                    </CardContent>
                  </Card>
                </> : <Card className="border-none shadow-xl h-full flex items-center justify-center">
                  <CardContent className="text-center py-20">
                    <Calculator className="h-24 w-24 text-gray-300 mx-auto mb-6" />
                    <h3 className="text-2xl font-bold text-gray-900 mb-4">
                      Ready to Calculate?
                    </h3>
                    <p className="text-gray-600 max-w-md mx-auto">
                      Enter your details in the form to get instant estimates for your solar installation, 
                      including system size, costs, subsidies, and savings.
                    </p>
                  </CardContent>
                </Card>
              }
            </div>
          </div>
        </div>
      </section>

      {/* Info Section */}
      <section className="py-16 px-4 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto max-w-4xl">
          <h2 className="text-3xl font-bold text-center mb-12">
            How We <span className="text-orange-500">Calculate</span>
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            {[
            {
              title: 'System Sizing',
              desc: 'Based on your monthly electricity bill and consumption patterns, we calculate the optimal solar system size for your needs.'
            },
            {
              title: 'Cost Estimation',
              desc: 'Includes solar panels, inverters, mounting structures, installation, and all necessary components with transparent pricing.'
            },
            {
              title: 'Subsidy Calculation',
              desc: 'We factor in government subsidies available for residential installations under PM SURYA GHAR as Rs.30,000 for 1 KW, Rs.60,000 for 2 KW and Rs.78,000 for 3 KW and above.'
            },
            {
              title: 'ROI Analysis',
              desc: 'Calculate payback period and long-term savings based on typical electricity rates and solar generation estimates.'
            }].
            map((item, index) =>
            <div key={index} className="bg-white p-6 rounded-xl shadow-lg">
                <h3 className="text-xl font-bold text-gray-900 mb-3">{item.title}</h3>
                <p className="text-gray-600 leading-relaxed">{item.desc}</p>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>);

};